import { useEffect, useState } from 'react';

import { useGraphQLMutation } from '@betterment/js-runtime';
import { Banner, Box, Button, Dialog, FormikField, Input } from '@betterment/samba-react';
import { Form, Formik } from 'formik';

import { SignablePackage } from '~/graphql/schema';

import { ForwardSignatureDocument } from './graphql/mutation.forward_package_signature.generated';

export interface ForwardSignatureDialogProps {
  signablePackageId: string;
  onForwardingComplete: (signablePackage: SignablePackage) => void;
}

interface ForwardSignatureFormValues {
  name: string;
  email: string;
}

export const ForwardSignatureDialog: React.FC<React.PropsWithChildren<ForwardSignatureDialogProps>> = ({
  signablePackageId,
  onForwardingComplete,
}) => {
  const [mutation, { data, called, error, reset, loading }] = useGraphQLMutation(ForwardSignatureDocument);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (called && data?.forwardPackageSignature?.result) {
      setIsOpen(false);
      onForwardingComplete(data.forwardPackageSignature.result);
    }
  }, [called, data]);

  const hasErrors = error || (data?.forwardPackageSignature?.displayableErrors?.length || 0) > 0;

  useEffect(() => {
    if (hasErrors) {
      setIsSubmitted(false);
    }
  }, [hasErrors]);

  const triggerMutation = (values: ForwardSignatureFormValues): void => {
    if (isSubmitted) return;
    setIsSubmitted(true);
    void mutation({ variables: { ...values, signablePackageId } });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={open => {
        if (loading) return;

        setIsOpen(open);
        if (!open) {
          reset();
        }
      }}
    >
      <Dialog.Trigger asChild={true}>
        <Button spacing="compact" variant="solidSecondary">
          Forward document
        </Button>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ p: 'space.400' }}>
        <Dialog.Title>Forward your plan document</Dialog.Title>
        {hasErrors && (
          <Banner
            css={{ width: '100%', mb: 'space.400' }}
            variant="critical"
            title="We could not forward your document. You may try resubmitting below."
          >
            We could not forward your document. You may try resubmitting below.
          </Banner>
        )}
        <Dialog.Description>
          If you are not the authorized signer, email the plan document via DocuSign® to the person who needs to sign.
        </Dialog.Description>
        <Dialog>
          <Formik initialValues={{ name: '', email: '' }} onSubmit={values => void triggerMutation(values)}>
            <Form role="form">
              <Box
                css={{
                  display: 'flex',
                  mb: 'space.400',
                  gap: 'space.100',
                }}
              >
                <Box css={{ width: '100%' }}>
                  <FormikField name="name">
                    <FormikField.Label>Name of signer</FormikField.Label>
                    <FormikField.Control>
                      <Input />
                    </FormikField.Control>
                  </FormikField>
                </Box>
                <Box css={{ width: '100%' }}>
                  <FormikField name="email">
                    <FormikField.Label>Email address of signer</FormikField.Label>
                    <FormikField.Control>
                      <Input placeholder="email@company.com" />
                    </FormikField.Control>
                  </FormikField>
                </Box>
              </Box>
              <Button
                loading={isSubmitted}
                type="submit"
                disabled={isSubmitted}
                css={{ height: 'size.300', width: '190px' }}
              >
                Forward document
              </Button>
            </Form>
          </Formik>
        </Dialog>
      </Dialog.Content>
    </Dialog>
  );
};
